import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information.svg';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { ZIPRECRUITER } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';

import { ZIPRECRUITER_BUDGET_PRESETS } from '../validation/constants';

interface ZipRecruiterBudgetProps {
  channelSlug: string;
}

export function ZipRecruiterBudget({ channelSlug }: ZipRecruiterBudgetProps) {
  const intl = useIntl();
  const { hookForm } = useJobPostingContext();
  const { watch, setValue } = hookForm;

  const openingBudgets = watch(`channels.${channelSlug}.openingBudgets`);
  const budgetAllocation = watch(`channels.${channelSlug}.budgetAllocation`);
  const channelBudget = watch(`channels.${channelSlug}.channelBudget`);

  const [zipRecruiterBudget, setZipRecruiterBudget] = useState<number>(
    ZIPRECRUITER_BUDGET_PRESETS[0].value,
  );

  const handleZipRecruiterApply = (selected: number[]) => {
    const value = selected[0];
    setZipRecruiterBudget(value);
    const openingCount = Object.keys(openingBudgets).length;
    setValue(`channels.${channelSlug}.channelBudget`, value * openingCount);
  };

  // Reset channel budget to default or saved value when switch budget allocation from 'per-funnel' to 'evenly' or return to channel details
  useEffect(() => {
    if (channelSlug === ZIPRECRUITER && budgetAllocation === 'evenly') {
      const budget =
        Object.values(openingBudgets).at(-1) ??
        ZIPRECRUITER_BUDGET_PRESETS[0].value;
      setZipRecruiterBudget(budget);
      const openingCount = Object.keys(openingBudgets).length;
      setValue(`channels.${channelSlug}.channelBudget`, budget * openingCount);
    }
  }, [budgetAllocation]);

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Typography variant="subtitle2">
            {budgetAllocation === 'evenly' ? (
              <FormattedMessage
                defaultMessage="Spend per opening"
                description="Label for budget input"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Total spend"
                description="Label for budget input"
              />
            )}{' '}
            <span className="text-fx-rose">*</span>
          </Typography>
          <Tooltip
            content={intl.formatMessage(
              {
                defaultMessage:
                  '{channelName} offers a fixed budget of {singleBoost} & {doubleBoost} per opening and cannot be changed.',
              },
              {
                channelName: getChannelName(channelSlug),
                singleBoost: ZIPRECRUITER_BUDGET_PRESETS[0].label,
                doubleBoost: ZIPRECRUITER_BUDGET_PRESETS[1].label,
              },
            )}
          >
            <img src={InfoIcon} alt="Info" />
          </Tooltip>
        </div>
        {budgetAllocation === 'evenly' ? (
          <Select<number>
            id="channel-budget-preset"
            options={ZIPRECRUITER_BUDGET_PRESETS}
            selectedValues={[zipRecruiterBudget]}
            onApply={handleZipRecruiterApply}
            isMultiSelect={false}
            placeholder="Select"
            className="rounded-sm"
          />
        ) : (
          <Input value={channelBudget ?? ''} readOnly placeholderLabel="$" />
        )}
      </div>
    </>
  );
}
